/**
 * Offline TTS Engine — Client-side speech synthesis using ONNX Runtime + Piper VITS models.
 * Uses @piper-plus/g2p for French phonemization (Text → IPA → Token IDs).
 * Falls back gracefully if model files are missing or inference fails.
 */

import { getVoiceFiles, isVoiceInstalledAsync } from '../hooks/useOfflineVoices';

// Cached sessions and configs per language
const sessionCache: Record<string, any> = {};
const configCache: Record<string, any> = {};
const encoderCache: Record<string, any> = {};
const phonemizerCache: Record<string, any> = {};

let ortModule: any = null;
let g2pModule: any = null;
let globalAudioContext: AudioContext | null = null;
let currentSource: AudioBufferSourceNode | null = null;

// ── Load ONNX Runtime dynamically from CDN ──

async function loadOrt(): Promise<any> {
  if (ortModule) return ortModule;
  try {
    ortModule = await import(/* @vite-ignore */ 'https://esm.sh/onnxruntime-web@1.21.0');
    // Point WASM files to jsdelivr CDN (must end with /)
    ortModule.env.wasm.numThreads = 1;
    ortModule.env.wasm.simd = true;
    ortModule.env.wasm.wasmPaths = 'https://cdn.jsdelivr.net/npm/onnxruntime-web@1.21.0/dist/';
    console.log('[OfflineTTS] ONNX Runtime loaded. WASM path:', ortModule.env.wasm.wasmPaths);
    return ortModule;
  } catch (err) {
    console.error('[OfflineTTS] Failed to load ONNX Runtime:', err);
    throw err;
  }
}

// ── Load @piper-plus/g2p for phonemization ──

async function loadG2P(): Promise<any> {
  if (g2pModule) return g2pModule;
  const cdnUrls = [
    'https://esm.sh/@piper-plus/g2p@0.4.0',
    'https://cdn.jsdelivr.net/npm/@piper-plus/g2p@0.4.0/+esm',
    'https://unpkg.com/@piper-plus/g2p@0.4.0?module'
  ];
  let lastErr: any = null;
  for (const url of cdnUrls) {
    try {
      g2pModule = await import(/* @vite-ignore */ url);
      console.log(`[OfflineTTS] @piper-plus/g2p loaded successfully from ${url}`);
      return g2pModule;
    } catch (err) {
      lastErr = err;
      console.warn(`[OfflineTTS] CDN ${url} failed to load @piper-plus/g2p, trying fallback...`, err);
    }
  }
  console.error('[OfflineTTS] All CDNs failed to load @piper-plus/g2p:', lastErr);
  throw lastErr;
}

// ── Decode ArrayBuffer to UTF-8 string ──

function decodeUtf8(buffer: ArrayBuffer): string {
  return new TextDecoder('utf-8').decode(new Uint8Array(buffer));
}

// ── Convert float32 audio samples to WAV ArrayBuffer ──

function float32ToWav(samples: Float32Array, sampleRate: number): ArrayBuffer {
  const numSamples = samples.length;
  const buffer = new ArrayBuffer(44 + numSamples * 2);
  const view = new DataView(buffer);

  const writeStr = (offset: number, str: string) => {
    for (let i = 0; i < str.length; i++) view.setUint8(offset + i, str.charCodeAt(i));
  };

  writeStr(0, 'RIFF');
  view.setUint32(4, 36 + numSamples * 2, true);
  writeStr(8, 'WAVE');
  writeStr(12, 'fmt ');
  view.setUint32(16, 16, true);
  view.setUint16(20, 1, true);      // PCM
  view.setUint16(22, 1, true);      // mono
  view.setUint32(24, sampleRate, true);
  view.setUint32(28, sampleRate * 2, true);
  view.setUint16(32, 2, true);      // block align
  view.setUint16(34, 16, true);     // bits per sample
  writeStr(36, 'data');
  view.setUint32(40, numSamples * 2, true);

  for (let i = 0; i < numSamples; i++) {
    const s = Math.max(-1, Math.min(1, samples[i]));
    view.setInt16(44 + i * 2, s < 0 ? s * 0x8000 : s * 0x7FFF, true);
  }

  return buffer;
}

// ── Main Engine ──

export const offlineTtsEngine = {
  /**
   * Check if offline TTS is available for a language.
   */
  isAvailable: async (langCode: string): Promise<boolean> => {
    const shortLang = langCode.split('-')[0].toLowerCase();
    return isVoiceInstalledAsync(shortLang);
  },

  /**
   * Speak text using the offline ONNX engine.
   * Pipeline: Text → G2P (IPA phonemes) → Encoder (phoneme IDs) → ONNX → WAV → Play
   * Returns true if audio was played successfully, false if fallback is needed.
   */
  speak: async (text: string, langCode: string, rate: number = 1.0): Promise<boolean> => {
    const shortLang = langCode.split('-')[0].toLowerCase();

    try {
      // 1. Get voice files from IndexedDB
      const files = await getVoiceFiles(shortLang);
      if (!files) return false;
      console.log("Arquivos recuperados do DB:", Object.keys(files));

      // 2. Parse config with explicit UTF-8 decoding
      if (!configCache[shortLang]) {
        const configStr = decodeUtf8(files.config);
        configCache[shortLang] = JSON.parse(configStr);
        console.log('[OfflineTTS] Config loaded for:', shortLang,
          '| sample_rate:', configCache[shortLang]?.audio?.sample_rate,
          '| phoneme_type:', configCache[shortLang]?.phoneme_type);
      }
      const config = configCache[shortLang];

      // 3. Initialize G2P phonemizer and Encoder (cached)
      if (!phonemizerCache[shortLang] || !encoderCache[shortLang]) {
        console.log("Iniciando carregamento do G2P...");
        const g2p = await loadG2P();

        // Create language-specific phonemizer
        if (shortLang === 'fr') {
          phonemizerCache[shortLang] = new g2p.FrenchG2P();
        } else if (shortLang === 'es') {
          phonemizerCache[shortLang] = new g2p.SpanishG2P();
        } else if (shortLang === 'pt') {
          phonemizerCache[shortLang] = new g2p.PortugueseG2P();
        } else if (shortLang === 'en') {
          phonemizerCache[shortLang] = new g2p.EnglishG2P();
        } else {
          // Fallback: try to create a G2P instance with this language
          try {
            const instance = await g2p.G2P.create({ languages: [shortLang] });
            phonemizerCache[shortLang] = instance;
          } catch {
            console.warn('[OfflineTTS] No G2P phonemizer for language:', shortLang);
            return false;
          }
        }

        // Create Encoder using config.json explicitly
        if (config?.phoneme_id_map) {
          encoderCache[shortLang] = new g2p.Encoder(config.phoneme_id_map);
          console.log("Motor Siwis carregado com sucesso via ONNX.JSON. IDs mapeados:", Object.keys(config.phoneme_id_map).length);
        } else {
          console.error('[OfflineTTS] No phoneme_id_map in config — cannot encode phonemes');
          return false;
        }
      }

      const phonemizer = phonemizerCache[shortLang];
      const encoder = encoderCache[shortLang];

      // 4. Phonemize text: Text → IPA phonemes
      const { tokens: phonemes } = phonemizer.phonemize(text);
      console.log('[OfflineTTS] Phonemes:', phonemes.join(' '));

      if (!phonemes || phonemes.length === 0) return false;

      // 5. Encode phonemes to IDs: IPA → phoneme_id_map → integer IDs
      const { phonemeIds } = encoder.encode(phonemes);
      console.log('[OfflineTTS] Phoneme IDs (first 30):', phonemeIds.slice(0, 30), '| total:', phonemeIds.length);

      if (phonemeIds.length < 3) return false;

      // 6. Load ONNX Runtime and create session (cached)
      if (!sessionCache[shortLang]) {
        const ort = await loadOrt();
        console.log('[OfflineTTS] Creating ONNX session... model size:', files.model.byteLength, 'bytes');
        sessionCache[shortLang] = await ort.InferenceSession.create(
          new Uint8Array(files.model),
          { executionProviders: ['wasm'] }
        );
        const sess = sessionCache[shortLang];
        console.log('[OfflineTTS] Model input names:', sess.inputNames);
        console.log('[OfflineTTS] Model output names:', sess.outputNames);
      }

      const session = sessionCache[shortLang];

      // 7. Prepare tensors
      const ort = await loadOrt();
      const inputTensor = new ort.Tensor('int64', BigInt64Array.from(phonemeIds.map(BigInt)), [1, phonemeIds.length]);
      const lengthTensor = new ort.Tensor('int64', BigInt64Array.from([BigInt(phonemeIds.length)]), [1]);

      const noiseScale = config?.inference?.noise_scale ?? 0.667;
      // Respect config length_scale, with a natural 1.04 multiplier for a relaxed, natural cadence
      const configLengthScale = config?.inference?.length_scale ?? 1.0;
      const lengthScale = (configLengthScale * 1.04) / rate;
      const noiseW = config?.inference?.noise_w ?? 0.8;
      const scalesTensor = new ort.Tensor('float32', Float32Array.from([noiseScale, lengthScale, noiseW]), [3]);

      // 8. Build feeds dynamically using actual model input names
      const inputNames = session.inputNames as string[];
      const feeds: Record<string, any> = {};
      for (const name of inputNames) {
        if (name.includes('length')) {
          feeds[name] = lengthTensor;
        } else if (name.includes('scale') || name === 'scales') {
          feeds[name] = scalesTensor;
        } else {
          feeds[name] = inputTensor;
        }
      }
      console.log('[OfflineTTS] Running inference with feeds:', Object.keys(feeds));

      // 9. Run inference
      const results = await session.run(feeds);
      const outputKey = Object.keys(results)[0];
      const rawData = results[outputKey].data;
      const audioData = rawData instanceof Float32Array ? rawData : new Float32Array(rawData);

      console.log('[OfflineTTS] Audio output: samples=', audioData.length,
        '| min=', Math.min(...audioData.slice(0, 1000)),
        '| max=', Math.max(...audioData.slice(0, 1000)));

      if (!audioData || audioData.length === 0) return false;

      // 10. Add silence pad and play via AudioContext (iOS compatible)
      // We use an 80ms silence padding (between 50ms and 100ms) at start and end of the Float32Array PCM
      const sampleRate = config?.audio?.sample_rate ?? 22050;
      const headPadLength = Math.floor(sampleRate * 0.08);
      const tailPadLength = Math.floor(sampleRate * 0.08);
      const paddedAudio = new Float32Array(audioData.length + headPadLength + tailPadLength);
      paddedAudio.set(audioData, headPadLength);

      if (!globalAudioContext) {
        const AudioContextClass = window.AudioContext || (window as any).webkitAudioContext;
        globalAudioContext = new AudioContextClass();
      }
      if (globalAudioContext.state === 'suspended') {
        await globalAudioContext.resume();
      }

      offlineTtsEngine.stop(); // Stop current if any

      const audioBuffer = globalAudioContext.createBuffer(1, paddedAudio.length, sampleRate);
      audioBuffer.getChannelData(0).set(paddedAudio);

      currentSource = globalAudioContext.createBufferSource();
      currentSource.buffer = audioBuffer;
      currentSource.connect(globalAudioContext.destination);

      await new Promise<void>((resolve) => {
        if (!currentSource) return resolve();
        currentSource.onended = () => { 
          currentSource = null; 
          resolve(); 
        };
        currentSource.start(0);
      });

      console.log('[OfflineTTS] Playback complete for:', shortLang);
      return true;

    } catch (err) {
      console.error("DEBUG REAL DO MOTOR:", err);
      console.warn('[OfflineTTS] Engine failed, falling back to browser TTS:', err);
      return false;
    }
  },

  /**
   * Stop any currently playing offline audio.
   */
  stop: () => {
    if (currentSource) {
      try { currentSource.stop(); } catch (e) {}
      currentSource = null;
    }
  },

  /**
   * Clear cached sessions to free memory.
   */
  clearCache: (langCode?: string) => {
    if (langCode) {
      const short = langCode.split('-')[0].toLowerCase();
      delete sessionCache[short];
      delete configCache[short];
      delete encoderCache[short];
      delete phonemizerCache[short];
    } else {
      Object.keys(sessionCache).forEach(k => delete sessionCache[k]);
      Object.keys(configCache).forEach(k => delete configCache[k]);
      Object.keys(encoderCache).forEach(k => delete encoderCache[k]);
      Object.keys(phonemizerCache).forEach(k => delete phonemizerCache[k]);
    }
  }
};
